import React, { useState, useCallback, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import type { IFormSchema, IFormElement, IDynamicFormTheme } from './types';
import { buildFieldValidation } from './utils/validators';
import { scrollToTop } from './utils/helpers';
import { mergeTheme } from './utils/theme';
import { FormStateProvider, useFormState } from './contexts/FormStateContext';
import { evaluateVisibility } from './utils/visibility';
import {
  FormProgressBar,
  FormNavigationButtons,
  FormPage,
  Loading,
  ErrorState,
  SuccessState,
} from './components';
import styles from './components/components.module.css';

export interface DynamicFormControlledProps {
  schema: IFormSchema | null;
  isLoading?: boolean;
  error?: string | null;
  isSuccess?: boolean;
  onSubmit: (data: Record<string, any>) => void | Promise<void>;
  onRetry?: () => void;
  userInfo?: Record<string, any>;
  initialValues?: Record<string, any>;
  config?: Partial<{
    loadingText?: string;
    successTitle?: string;
    successMessage?: string;
    showHeader?: boolean;
    showProgressBar?: boolean;
    showNavigation?: boolean;
    prevButtonText?: string;
    nextButtonText?: string;
    submitButtonText?: string;
  }>;
  callbacks?: Partial<{
    onValidationError?: (errors: any) => void;
    onPageChange?: (newPage: number, oldPage: number) => void;
    onFormValuesChange?: (values: Record<string, any>) => void;
  }>;
  theme?: Partial<IDynamicFormTheme>;
  className?: string;
}

export const DynamicFormControlled: React.FC<DynamicFormControlledProps> = ({
  schema,
  isLoading = false,
  error = null,
  isSuccess = false,
  onSubmit,
  onRetry,
  userInfo,
  initialValues = {},
  config = {},
  callbacks = {},
  theme = {},
  className,
}) => {
  return (
    <FormStateProvider>
      <DynamicFormControlledInner
        schema={schema}
        isLoading={isLoading}
        error={error}
        isSuccess={isSuccess}
        onSubmit={onSubmit}
        onRetry={onRetry}
        userInfo={userInfo}
        initialValues={initialValues}
        config={config}
        callbacks={callbacks}
        theme={theme}
        className={className}
      />
    </FormStateProvider>
  );
};

const DynamicFormControlledInner: React.FC<DynamicFormControlledProps> = ({
  schema,
  isLoading = false,
  error = null,
  isSuccess = false,
  onSubmit,
  onRetry,
  userInfo,
  initialValues = {},
  config = {},
  callbacks = {},
  theme = {},
  className,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formStateContext = useFormState();

  const mergedTheme = useMemo(() => mergeTheme(theme), [theme]);

  const buildPageValidationSchema = useCallback(
    (pageIndex: number) => {
      if (!schema || !schema.pages || !schema.pages[pageIndex]) {
        return yup.object().shape({}) as yup.AnyObjectSchema;
      }

      const page = schema.pages[pageIndex];
      const schemaFields: Record<string, any> = {};

      const processElement = (element: IFormElement) => {
        const fieldSchema = buildFieldValidation(element);
        if (fieldSchema) {
          schemaFields[element.name] = fieldSchema;
        }

        if (element.elements) {
          element.elements.forEach(processElement);
        }
      };

      page.elements?.forEach(processElement);

      return yup.object().shape(schemaFields);
    },
    [schema]
  );

  const validationSchema = useMemo(
    () => buildPageValidationSchema(currentPage),
    [buildPageValidationSchema, currentPage]
  );

  const resolver = useMemo(
    () => yupResolver(validationSchema as any) as any,
    [validationSchema]
  );

  const methods = useForm<any>({
    mode: 'onChange',
    resolver,
    defaultValues: { ...initialValues, ...userInfo },
  });

  const {
    trigger,
    getValues,
    formState: { errors },
    watch,
  } = methods;

  React.useEffect(() => {
    const subscription = watch((values) => {
      callbacks.onFormValuesChange?.(values);
    });
    return () => subscription.unsubscribe();
  }, [watch, callbacks]);

  const handlePrevious = useCallback(() => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
      scrollToTop();
    }
  }, [currentPage]);

  const handleNext = useCallback(async () => {
    const isValid = await trigger();

    if (!isValid) {
      callbacks.onValidationError?.(errors);
      return;
    }

    if (schema && currentPage < schema.pages.length - 1) {
      setCurrentPage((prev) => prev + 1);
      scrollToTop();
      callbacks.onPageChange?.(currentPage + 1, currentPage);
    }
  }, [currentPage, schema, trigger, errors, callbacks]);

  const handleFormSubmit = useCallback(async () => {
    formStateContext.markSubmitAttempted();

    const formData = getValues();
    const validationErrors: Record<string, string> = {};

    if (schema && schema.pages) {
      const validateElement = (element: IFormElement) => {
        const isVisible =
          !element.visibleIf || evaluateVisibility(element.visibleIf, formData);

        if (!isVisible) return;

        if (element.isRequired) {
          const value = formData[element.name];
          const isEmpty =
            value === undefined ||
            value === null ||
            value === '' ||
            (Array.isArray(value) && value.length === 0);

          if (isEmpty) {
            validationErrors[element.name] = 'Este campo é obrigatório.';
          }
        }

        if (element.elements) {
          element.elements.forEach(validateElement);
        }
      };

      const currentPageData = schema.pages[currentPage];
      if (currentPageData && currentPageData.elements) {
        currentPageData.elements.forEach(validateElement);
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      formStateContext.setValidationErrors(validationErrors);
      callbacks.onValidationError?.(validationErrors);
      scrollToTop();
      return;
    }

    formStateContext.setValidationErrors({});

    const isValid = await trigger();

    if (!isValid) {
      callbacks.onValidationError?.(errors);
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(formData);
    } catch (err) {
      console.error('Error submitting form:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    trigger,
    errors,
    getValues,
    onSubmit,
    callbacks,
    formStateContext,
    schema,
    currentPage,
  ]);

  const themeStyle: React.CSSProperties = {
    ['--df-primary-color' as any]: mergedTheme.primaryColor,
    ['--df-primary-color-light' as any]: `${mergedTheme.primaryColor}1a`,
    ['--df-primary-color-dark' as any]: mergedTheme.primaryColor,
    ['--df-error-color' as any]: mergedTheme.errorColor,
    ['--df-text-primary' as any]: mergedTheme.textPrimary,
    ['--df-text-secondary' as any]: mergedTheme.textSecondary,
    ['--df-font-family' as any]: mergedTheme.fontFamily,
  };

  if (isLoading) {
    return (
      <div
        className={`${styles.formContainer} ${className || ''}`}
        style={themeStyle}
      >
        <Loading text={config.loadingText || 'Carregando formulário...'} />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`${styles.formContainer} ${className || ''}`}
        style={themeStyle}
      >
        <ErrorState message={error} onRetry={onRetry} />
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div
        className={`${styles.formContainer} ${className || ''}`}
        style={themeStyle}
      >
        <SuccessState
          title={config.successTitle}
          message={config.successMessage}
        />
      </div>
    );
  }

  if (!schema) {
    return (
      <div
        className={`${styles.formContainer} ${className || ''}`}
        style={themeStyle}
      >
        <ErrorState
          message="Schema do formulário não foi fornecido"
          onRetry={onRetry}
        />
      </div>
    );
  }

  const totalPages = schema.pages?.length || 0;
  const currentPageData = schema.pages?.[currentPage];

  return (
    <div
      className={`${styles.formContainer} ${className || ''}`}
      style={themeStyle}
    >
      {config.showProgressBar !== false && totalPages > 1 && (
        <FormProgressBar currentPage={currentPage} totalPages={totalPages} />
      )}

      <FormProvider {...methods}>
        <form onSubmit={(e) => e.preventDefault()}>
          {currentPageData && (
            <FormPage
              page={currentPageData}
              pageIndex={currentPage}
              totalPages={totalPages}
            />
          )}

          {config.showNavigation !== false && (
            <FormNavigationButtons
              currentPage={currentPage}
              totalPages={totalPages}
              isSubmitting={isSubmitting}
              onPrevious={handlePrevious}
              onNext={handleNext}
              onSubmit={handleFormSubmit}
              prevText={config.prevButtonText}
              nextText={config.nextButtonText}
              submitText={config.submitButtonText}
            />
          )}
        </form>
      </FormProvider>
    </div>
  );
};

export default DynamicFormControlled;
